import { useMutation } from "@tanstack/react-query";
import DatasetGroupsApi from "api/DatasetGroupsApi";
import { CreateDatasetGroup, DatasetGroup } from "api/model/dataset/datasetGroups";
import { queryClient } from "services/HttpService";
import { formatGroupsOnUpdate } from "components/dashboard/dataset-window/datasets-menu/dataset-groups/dataset-group-utils";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { IQueryOptions, QueryKey, defaultResponseHandler, useQueryWrapper } from "./api-hooks-common";

const HOUR_IN_MILLIS = 1000 * 60 * 60;
const DAY_IN_MILLIS = HOUR_IN_MILLIS * 24;

const useGetDatasetGroups = (options?: IQueryOptions) =>
  useQueryWrapper([QueryKey.DATASET_GROUPS], () => DatasetGroupsApi.getAll(), null, {
    ...options,
    staleTime: options?.staleTime ?? HOUR_IN_MILLIS,
    gcTime: options?.gcTime ?? DAY_IN_MILLIS,
  });

const useGetDatasetGroupById = (id: number, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_GROUPS, id.toString()],
    () => DatasetGroupsApi.getById(id),
    null,
    {
      ...options,
      staleTime: options?.staleTime ?? HOUR_IN_MILLIS,
      gcTime: options?.gcTime ?? DAY_IN_MILLIS,
    },
    queryClient.getQueryData([QueryKey.DATASET_GROUPS, id]) as DatasetGroup
  );

const useCreateDatasetGroup = () => {
  return useMutation({
    mutationFn: async (data: CreateDatasetGroup) => {
      const res = await DatasetGroupsApi.create(data);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.DATASET_GROUPS], (oldQueryData: DatasetGroup[]) => {
        if (!data) return oldQueryData;
        return formatGroupsOnUpdate(oldQueryData, data);
      });
      easasAlert({ summary: "Dataset group created successfully", severity: "success" });
    },
    onError: error => {
      easasAlert({
        summary: "Dataset group could not be created",
        detail: error.message,
        severity: "error",
      });
    },
  });
};

const useUpdateDatasetGroup = () => {
  return useMutation({
    mutationFn: async (data: DatasetGroup) => {
      const res = await DatasetGroupsApi.update(data.id, data);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.DATASET_GROUPS, data?.id.toString()], data);
      queryClient.setQueryData([QueryKey.DATASET_GROUPS], (oldQueryData: DatasetGroup[]) => {
        if (!data) return oldQueryData;
        return formatGroupsOnUpdate(oldQueryData, data);
      });
      easasAlert({ summary: "Dataset group updated successfully", severity: "success" });
    },
    onError: error => {
      easasAlert({
        summary: "Dataset group could not be updated",
        detail: error.message,
        severity: "error",
      });
    },
  });
};

const useDeleteDatasetGroup = () => {
  return useMutation({
    mutationFn: async (data: DatasetGroup) => {
      const res = await DatasetGroupsApi.remove(data.id);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.DATASET_GROUPS], (oldQueryData: DatasetGroup[]) => {
        if (!data) return oldQueryData;
        return formatGroupsOnUpdate(oldQueryData, data, true);
      });
      queryClient.removeQueries({ exact: true, queryKey: [QueryKey.DATASET_GROUPS, data?.id.toString()] });
      easasAlert({ summary: "Dataset group deleted successfully", severity: "success" });
    },
    onError: error => {
      easasAlert({
        summary: "Dataset group could not be deleted",
        detail: error.message,
        severity: "error",
      });
    },
  });
};

export {
  useCreateDatasetGroup,
  useDeleteDatasetGroup,
  useGetDatasetGroupById,
  useGetDatasetGroups,
  useUpdateDatasetGroup,
};
